import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';
import { FLEET } from '../config.js';

// ---------------------------------------------------------------------------
// A tjalk — the flat-bottomed sailing barge that actually works this river.
//
// The reference for the rig is a Minecraft galleon: stepped triangular sail,
// tan deck over a dark hull, a pennant at the masthead. A galleon at true
// scale would be longer than the village and could not turn in a canal ten
// voxels wide, so what is kept is the silhouette, not the size.
// ---------------------------------------------------------------------------

export function registerBoatBlocks(palette) {
    return {
        hull: palette.add('tjalkHull', 0x6e3730, { jitter: 0.07 }),
        strake: palette.add('tjalkStrake', 0x8c5a3c, { jitter: 0.06 }),
        deck: palette.add('tjalkDeck', 0xc3a370, { jitter: 0.07 }),
        rail: palette.add('tjalkRail', 0xd8bd8c, { jitter: 0.06 }),
        mast: palette.add('tjalkMast', 0x6b4f2a, { jitter: 0.06 }),
        sail: palette.add('tjalkSail', 0xe9e4d6, { jitter: 0.035 }),
        flag: palette.add('tjalkFlag', 0xc4482f, { jitter: 0.05 }),
        lamp: palette.add('tjalkLamp', 0xffc978, { emissive: 1.0, jitter: 0.02 }),
        // One palette entry per flag in the fleet, registered up front so a
        // boat arriving mid-session costs a mesh and not a palette rebuild.
        flags: FLEET.flags.map((hex, i) => palette.add(`fleetFlag${i}`, hex, { jitter: 0.04 })),
    };
}

const L = 17;   // length, along local +X (bow at high X)
const B = 7;    // beam, along local Z
const H = 20;

/**
 * Builds the boat with the waterline at local y = 0, so the caller only has to
 * put the group at sea level. Returns the mesh plus the parts that move.
 */
export function createSailboat(palette, material, blocks, { voxelSize, flagId = null }) {
    const P = blocks;

    // --- hull -------------------------------------------------------------
    const hullGrid = new VoxelGrid(L, H, B);
    const cz = (B - 1) / 2;

    for (let x = 0; x < L; x++) {
        // Bow tapers over the forward third; the stern stays full and square,
        // which is what makes a tjalk a tjalk.
        const fromBow = (L - 1 - x) / (L - 1);
        const taper = Math.round(Math.max(0, (0.34 - fromBow) / 0.34) * 2.2);
        const z0 = 1 + taper;
        const z1 = B - 2 - taper;
        if (z0 > z1) continue;

        hullGrid.box(x, 0, z0, x, 1, z1, P.hull);      // below the waterline
        hullGrid.box(x, 2, z0, x, 2, z1, P.strake);    // rubbing strake
        hullGrid.box(x, 3, z0, x, 3, z1, P.deck);      // deck

        // Bulwark, open amidships where you would step aboard.
        const gangway = x >= 6 && x <= 8;
        if (!gangway) {
            hullGrid.set(x, 4, z0, P.rail);
            hullGrid.set(x, 4, z1, P.rail);
        }
    }

    // Stem and sternpost, a voxel proud of the rail.
    hullGrid.box(L - 1, 3, 2, L - 1, 5, B - 3, P.strake);
    hullGrid.box(0, 3, 1, 0, 5, B - 2, P.strake);

    // Leeboards — the sideways-hanging boards a flat-bottomed boat needs to
    // stop it sliding downwind. Nothing else on the river has them.
    hullGrid.box(7, 1, 0, 9, 3, 0, P.mast);
    hullGrid.box(7, 1, B - 1, 9, 3, B - 1, P.mast);

    // A cabin aft, with a lit window.
    hullGrid.box(1, 4, 2, 4, 5, B - 3, P.deck);
    hullGrid.box(1, 6, 2, 4, 6, B - 3, P.strake);
    hullGrid.set(2, 5, 1, P.lamp);
    hullGrid.set(3, 5, B - 2, P.lamp);

    const hullMesh = new THREE.Mesh(
        hullGrid.build(palette, {
            voxelSize,
            // Waterline at y = 0, and the boat balanced about its middle.
            origin: new THREE.Vector3(-(L / 2) * voxelSize, -2 * voxelSize, -(B / 2) * voxelSize),
        }),
        material,
    );

    // --- rig, on its own pivot so the sail can swing with the wind --------
    const MAST = 13;
    const rigGrid = new VoxelGrid(L, MAST + 3, B);
    const mx = 10;   // mast a little forward of amidships

    for (let y = 0; y < MAST; y++) rigGrid.set(mx, y, cz, P.mast);

    // Boom along the deck, running aft from the foot of the mast.
    rigGrid.box(mx - 8, 1, cz, mx - 1, 1, cz, P.mast);

    // The sail: a stepped triangle, tall at the mast and tapering aft, exactly
    // the shape the reference gets by stacking blocks a row at a time.
    for (let y = 2; y < MAST - 1; y++) {
        const up = (y - 2) / (MAST - 3);           // 0 at the boom, 1 at the head
        const reach = Math.round((1 - up) * 8);    // how far aft the cloth runs
        if (reach < 1) continue;
        rigGrid.box(mx - reach, y, cz, mx - 1, y, cz, P.sail);
    }

    // A small square topsail forward, and the pennant above it.
    for (let y = MAST - 6; y < MAST - 1; y++) {
        rigGrid.box(mx + 1, y, cz, mx + 3, y, cz, P.sail);
    }

    // The pennant is the one part that changes after the boat is built: your
    // own colour is handed out by the relay, which has not answered yet when
    // the world is made. Rebuilding the rig is cheap and happens once.
    const rigOrigin = new THREE.Vector3(-mx * voxelSize, 2 * voxelSize, -cz * voxelSize);
    const buildRig = (flag) => {
        rigGrid.box(mx, MAST, cz, mx + 2, MAST + 1, cz, flag);
        return rigGrid.build(palette, { voxelSize, origin: rigOrigin });
    };
    const rig = new THREE.Mesh(buildRig(flagId ?? P.flag), material);
    // Pivot at the mast: rotating the rig swings the whole sail, as sheeting
    // the boom in and out would.
    const rigPivot = new THREE.Group();
    rigPivot.position.set((mx - L / 2) * voxelSize, 0, 0);
    rigPivot.add(rig);

    // The hull is modelled bow-along-+X because that is the natural way to lay
    // out a grid, but everything outside this file steers in the world's terms,
    // where a heading of 0 points along +Z. This inner group reconciles the
    // two: turn it a quarter turn and the boat's bow IS the group's forward.
    // Without it she makes way ninety degrees off her heading — crabbing
    // sideways across the water like a skater — and heel and pitch land on
    // each other's axes.
    const hull = new THREE.Group();
    hull.rotation.y = -Math.PI / 2;
    hull.add(hullMesh);
    hull.add(rigPivot);

    const group = new THREE.Group();
    group.add(hull);
    group.name = 'sailboat';

    return {
        group,
        rigPivot,
        length: L * voxelSize,
        beam: B * voxelSize,
        /** Fly a different flag — used once, when the relay says which. */
        setFlag(flag) {
            const old = rig.geometry;
            rig.geometry = buildRig(flag);
            old.dispose();
        },
    };
}
